import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import {MediaQueryService} from '@mydocma/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ReportService} from '../reports.service';
import {ReportStepperComponent} from '../stepper/stepper/report-stepper.component';
import {MmProjectService} from '../../../../mm-project/mm-project.service';
import {MmLayoutStateService} from '../../../../mm-filter/layout/layout-state.service';
import {FaultReportConfig} from '../faults-report.config';
import {ReportCreationTypeEnum} from '../common/report-creation-type.enum';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {ReportOptionsService} from '../stepper/services/report-options.service';
import {AppContainerService} from '@mydocma/app-container';
import { getIosMajorVersionCapacitor, getIosOsMajorVersion, getSafariMajorVersion, isIosDevice } from '@docma-mm-core/utils/utils';
import { NativeService } from '@mydocma/core/src/lib/native';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-report-dialog',
  templateUrl: './report-dialog.component.html',
  styleUrls: ['./report-dialog.component.scss'],
})
export class ReportDialogComponent implements OnInit, AfterViewInit {

  mobileMode: boolean;
  creationType = ReportCreationTypeEnum;

  loadStatusEnum = LoadStatusEnum;

  @ViewChild('stepperComponent', { static: false })
  private stepperComponent: ReportStepperComponent;

  private static readonly ANIMATION_UNSUPPORTED_ENGINE_VERSIONS: number[] = [26];
  public isAnimationUnsupportedDevice: boolean;
  public readonly engineMajorVersion: number | null;
  public iosOsMajorVersion: number | null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: {config: FaultReportConfig, service: ReportService},
    public readonly mediaQueryService: MediaQueryService,
    public readonly projectService: MmProjectService,
    public readonly appContainerService: AppContainerService,
    public readonly reportOptionsService: ReportOptionsService,
    public readonly layoutStateService: MmLayoutStateService,
    public readonly nativeService: NativeService) {
      this.engineMajorVersion = getSafariMajorVersion();
      this.iosOsMajorVersion = getIosOsMajorVersion();

      this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();
  }

  async ngOnInit() {
    if(this.nativeService.isIos){
      this.iosOsMajorVersion = await getIosMajorVersionCapacitor();
      this.isAnimationUnsupportedDevice = this.computeIsAnimationUnsupportedDevice();
    }
   }

  ngAfterViewInit() {
    if (this.stepperComponent) {
      this.stepperComponent.stepperService$
        .pipe(untilDestroyed(this))
        .subscribe(service => {
          this.dialogData.service.stepperService = service;
        });
    }
  }

  private computeIsAnimationUnsupportedDevice(): boolean {
    if (!isIosDevice()) {
      return false;
    }
    const versionToCheck = this.iosOsMajorVersion ?? this.engineMajorVersion;
    if (versionToCheck == null) {
      return false;
    }
    return ReportDialogComponent.ANIMATION_UNSUPPORTED_ENGINE_VERSIONS.includes(versionToCheck);
  }

  onResize(event) {
    this.mobileMode = event.contentRect.width <= 600;
  }

  async next() {
    try{
      await this.dialogData.service.stepperService.nextStep();
    }catch{}

    const currentStep = this.dialogData.service.stepperService.currStep;
    if((this.dialogData.config.creationType === ReportCreationTypeEnum.download && currentStep.config.name === 'selectCreationType') ||
      (this.dialogData.config.creationType === ReportCreationTypeEnum.email && currentStep.config.name === 'selectEmail') ){
      this.dialogData.service.createReport(this.dialogData.config);
    }
  }

  cancel() {
    this.dialogData.service.cancelReportConfiguration(this.dialogData.config);
  }

  async createPreview() {
    const enableLlLog = (this.dialogData.config.options?.EnableLlLog ?? false);
    this.dialogData.config.options = this.reportOptionsService.getOptionsValue();
    this.dialogData.config.options.EnableLlLog = enableLlLog;
    this.dialogData.config.previewModel = await this.dialogData.service.createPreview(this.dialogData.config);
  }

  downloadPreview() {
    this.dialogData.service.downloadPreview(this.dialogData.config);
  }

}
